import { resolveBidderPreset } from '@auctisync/shared';
import { Logo } from '@auctisync/ui';
import { useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useTenantBrandStyle } from '../../hooks/useTenantBrandStyle';

/**
 * Onboarding preview frame (spec §4, Phase E1). Renders the reused public
 * catalog components (via <Outlet/>) themed with the *draft* tenant's branding,
 * inside a persistent "PREVIEW" banner so the owner can review their storefront
 * before flipping live. The tenant is injected as the selected house by
 * <BidderHousesProvider overrideHouse>, so useTenantBrandStyle themes the frame
 * exactly as the live bidder site will look.
 *
 * This is NOT the live site: no bidder chrome (sign-out / my-dashboard / bell),
 * because the viewer is the owner reviewing, not a bidder.
 */
export function PreviewLayout() {
  const { selected } = useBidderHouses();
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const brandStyle = useTenantBrandStyle(selected?.branding, mode);
  // The preview must show the DRAFT house's chosen art direction, or an owner
  // reviewing their storefront is reviewing somebody else's.
  const preset = resolveBidderPreset(
    (selected?.branding as { preset?: unknown } | null | undefined)?.preset,
  );
  const houseName = selected?.display_name || selected?.name || 'Your auction house';

  return (
    <div
      data-theme="bidder"
      /* The house's art direction. Type and shape only — never colour, never
         the position of a box — so a tenant's choice cannot break a page.
         `resolveBidderPreset` falls back to modern rather than passing an
         unknown value to a CSS selector that would match nothing. */
      data-preset={preset}
      data-mode={mode}
      className="min-h-screen"
      style={{ ...brandStyle, background: 'var(--surface)', color: 'var(--text)' }}
    >
      {/* Preview banner — always visible, uses --system-* so it reads as
          AuctiSync chrome, not part of the tenant's themed site. */}
      <div
        role="status"
        style={{
          background: 'var(--system-brand-primary, #2264DC)',
          color: 'var(--system-brand-primary-fg, #ffffff)',
          padding: '0.6rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          fontSize: '0.85rem',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <strong
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.72rem',
              padding: '0.12rem 0.5rem',
              borderRadius: '9999px',
              background: 'rgba(255,255,255,0.18)',
            }}
          >
            Preview
          </strong>
          <span>
            This is how <strong>{houseName}</strong>&rsquo;s site will look to bidders. Not live
            yet.
          </span>
        </span>
        <Link
          to="/admin/onboarding"
          style={{
            color: 'inherit',
            textDecoration: 'underline',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          ← Back to setup
        </Link>
      </div>

      {/* Tenant-branded header — mirrors the live bidder header's shell so the
          preview is faithful, minus the personal bidder nav. */}
      <header
        style={{
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface-elev)',
          padding: '0.75rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text)' }}>
            <Logo height={22} />
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{houseName}</span>
        </div>
        <button
          type="button"
          onClick={() => setMode((m) => (m === 'dark' ? 'light' : 'dark'))}
          aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={mode === 'dark' ? 'Light mode' : 'Dark mode'}
          style={{
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-control, 0.375rem)',
            width: '2rem',
            height: '1.9rem',
            padding: 0,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            fontSize: '0.95rem',
            lineHeight: 1,
          }}
        >
          {mode === 'dark' ? '☀' : '☾'}
        </button>
      </header>

      <Outlet />
    </div>
  );
}
